
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Save } from "lucide-react";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { useToast } from "@/components/ui/use-toast";
import { ContaBancaria } from "@/types/financeiro";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Mock list of banks
const bancos = [
  "Banco do Brasil",
  "Bradesco",
  "Caixa Econômica Federal",
  "Itaú",
  "Santander",
  "Nubank",
  "Inter",
  "BTG Pactual",
  "Sicoob",
  "Outro"
];

interface CadastroContaBancariaProps {
  onClose: () => void;
  contaParaEditar?: ContaBancaria;
}

const CadastroContaBancaria: React.FC<CadastroContaBancariaProps> = ({ 
  onClose,
  contaParaEditar
}) => {
  const [banco, setBanco] = useState<string>(contaParaEditar?.banco || '');
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<Omit<ContaBancaria, 'id' | 'banco'>>({
    defaultValues: contaParaEditar ? {
      agencia: contaParaEditar.agencia,
      numero: contaParaEditar.numero,
      descricao: contaParaEditar.descricao,
      saldoInicial: contaParaEditar.saldoInicial,
      saldoAtual: contaParaEditar.saldoAtual,
      ativo: contaParaEditar.ativo
    } : {
      agencia: '',
      numero: '',
      descricao: '',
      saldoInicial: 0,
      saldoAtual: 0,
      ativo: true
    }
  });
  const { toast } = useToast();
  const { user } = useAuth();

  const onSubmit = async (data: Omit<ContaBancaria, 'id' | 'banco'>) => {
    if (!banco) {
      toast({
        title: "Erro",
        description: "Selecione um banco",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    if (!user) {
      toast({
        title: "Erro",
        description: "Usuário não autenticado",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    // Prevent multiple submissions
    setSubmitting(true);

    try {
      // Create new account object
      const accountId = contaParaEditar?.id || uuidv4();
      const saldoAtual = contaParaEditar ? data.saldoAtual : data.saldoInicial;

      console.log("Salvando conta bancária:", {
        id: accountId,
        banco,
        agencia: data.agencia,
        numero: data.numero,
        descricao: data.descricao,
        saldoInicial: data.saldoInicial,
        saldoAtual,
        user_id: user.id
      });

      // Upsert to Supabase
      const { error } = await supabase
        .from('contas_bancarias')
        .upsert({
          id: accountId,
          banco: banco,
          agencia: data.agencia,
          numero: data.numero,
          descricao: data.descricao,
          saldo_inicial: data.saldoInicial,
          saldo_atual: saldoAtual,
          ativo: data.ativo,
          user_id: user.id
        });
      
      if (error) {
        console.error("Erro ao salvar conta:", error);
        throw error;
      }
      
      toast({
        title: "Conta salva",
        description: "As informações da conta foram salvas com sucesso",
        duration: 3000,
      });
      
      onClose();
    } catch (error) {
      console.error("Erro ao salvar conta:", error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar a conta bancária",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border border-financeiro-border-light">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg font-semibold text-financeiro-text-DEFAULT">
          {contaParaEditar ? 'Editar Conta Bancária' : 'Nova Conta Bancária'}
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="banco" className="text-xs">Banco</Label>
              <Select value={banco} onValueChange={setBanco}>
                <SelectTrigger id="banco" className="text-xs">
                  <SelectValue placeholder="Selecione um banco" />
                </SelectTrigger>
                <SelectContent>
                  {bancos.map((b) => (
                    <SelectItem key={b} value={b} className="text-xs">{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="descricao" className="text-xs">Descrição</Label>
              <Input 
                id="descricao" 
                placeholder="Ex: Conta Corrente Principal"
                className="text-xs"
                {...register("descricao", { required: "Descrição é obrigatória" })}
              />
              {errors.descricao && (
                <p className="text-red-500 text-xs">{errors.descricao.message}</p>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="agencia" className="text-xs">Agência</Label>
              <Input 
                id="agencia" 
                placeholder="Número da agência"
                className="text-xs"
                {...register("agencia", { required: "Agência é obrigatória" })}
              />
              {errors.agencia && (
                <p className="text-red-500 text-xs">{errors.agencia.message}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="numero" className="text-xs">Conta</Label>
              <Input 
                id="numero" 
                placeholder="Número da conta com dígito"
                className="text-xs"
                {...register("numero", { required: "Número da conta é obrigatório" })}
              />
              {errors.numero && (
                <p className="text-red-500 text-xs">{errors.numero.message}</p>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="saldoInicial" className="text-xs">Saldo Inicial</Label>
              <Input 
                id="saldoInicial" 
                type="number" 
                step="0.01"
                className="text-xs"
                placeholder="0,00"
                {...register("saldoInicial", { valueAsNumber: true })}
              />
            </div>
          </div>
        </CardContent>
        
        <CardFooter className="flex justify-between border-t border-financeiro-border-light pt-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
            className="border-financeiro-border-DEFAULT text-financeiro-text-DEFAULT text-xs"
          >
            Cancelar
          </Button>
          <Button 
            type="submit"
            className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark flex items-center text-xs"
            disabled={submitting}
          >
            <Save className="h-4 w-4 mr-1" />
            {submitting ? 'Salvando...' : (contaParaEditar ? 'Atualizar' : 'Cadastrar')} Conta
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default CadastroContaBancaria;
